import type { AccordionItem } from '../common/Accordion';
import { SpecTable } from './SpecTable';
import { Icon } from '../common/Icon';
import { cn } from '../../lib/cn';
import type { DetailSection } from '../../types';

/**
 * Adapts the BACKEND's free-form product-detail sections (the ones staff author in the dashboard —
 * TEXT / BULLETS / TABLE / KEYVALUE, with a free-text title) into the premium accordion, rendered by
 * `kind` and skinned to match the reference design. The section's title becomes the accordion heading;
 * a best-effort recogniser picks a fitting icon + auto-opens Dosage/Composition. Nothing authored in
 * the dashboard is dropped — the backend is the single source of truth for this content.
 */

function iconFor(title: string, kind: DetailSection['kind']): string {
  const t = title.toLowerCase();
  if (/dosage|dose|schedule|programme|application programme/.test(t)) return 'Beaker';
  if (/composition|formula|ingredient/.test(t)) return 'FlaskConical';
  if (/spec|technical|parameter/.test(t)) return 'Gauge';
  if (/benefit/.test(t)) return 'CheckCircle2';
  if (/usage|how to use|direction|method|apply/.test(t)) return 'Droplets';
  if (/precaution|caution|safety|warning|storage/.test(t)) return 'ShieldAlert';
  if (/licen|certif|approval|registration/.test(t)) return 'ShieldCheck';
  if (/packag/.test(t)) return 'Package';
  if (/warrant/.test(t)) return 'ShieldCheck';
  if (/crop/.test(t)) return 'Sprout';
  return kind === 'TABLE' ? 'Gauge' : kind === 'BULLETS' ? 'Check' : 'Info';
}

function splitParas(text?: string): string[] {
  return (text ?? '')
    .split(/\n{2,}|\r\n\r\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function GenericTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  if (!columns.length) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-sand-200">
      <table className="w-full min-w-[30rem] border-collapse text-sm">
        <thead>
          <tr className="bg-sand-100/70 text-left">
            {columns.map((c, i) => (
              <th key={i} scope="col" className="px-4 py-3 font-semibold text-ink-700">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-t border-sand-200 odd:bg-white even:bg-canvas/60">
              {columns.map((_, ci) => (
                <td key={ci} className="px-4 py-3 align-top text-ink-700">
                  {row[ci] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Render every content part a section carries, in a fixed order — so one accordion can consolidate
 * a lead paragraph + a spec/data table + method chips + a step/bullet list + a highlighted note,
 * matching the reference's richer "Composition" / "Dosage & application" / "Packaging…" sections.
 * A plain single-kind section (only one part populated) renders exactly as before.
 */
function renderSection(s: DetailSection): React.ReactNode {
  const paras = splitParas(s.text);
  return (
    <div className="space-y-5 leading-relaxed">
      {paras.length > 0 && (
        <div className="space-y-3">
          {paras.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      )}

      {s.imageUrl && <img src={s.imageUrl} alt="" loading="lazy" className="rounded-lg border border-sand-200" />}

      {s.pairs?.length ? (
        <SpecTable rows={s.pairs.map((p) => ({ parameter: p.label, value: p.value }))} caption={s.title} />
      ) : null}

      {s.columns?.length ? (
        <div>
          <GenericTable columns={s.columns} rows={s.rows ?? []} />
          {s.caption && <p className="mt-2 text-xs text-ink-500">{s.caption}</p>}
        </div>
      ) : null}

      {s.chips?.length ? (
        <div>
          {s.chipsLabel && <p className="mb-2 text-sm font-semibold text-ink-700">{s.chipsLabel}</p>}
          <div className="flex flex-wrap gap-2">
            {s.chips.map((c) => (
              <span key={c} className="rounded-full border border-sand-200 bg-canvas px-3 py-1 text-sm text-ink-700">
                {c}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {s.items?.length ? (
        s.ordered ? (
          <ol className="space-y-3">
            {s.items.map((it, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-600">
                  {i + 1}
                </span>
                <span>{it}</span>
              </li>
            ))}
          </ol>
        ) : (
          <ul className="space-y-2.5">
            {s.items.map((it, i) => (
              <li key={i} className="flex gap-3">
                <Icon
                  name={s.tone === 'warn' ? 'AlertCircle' : 'Check'}
                  className={cn('mt-0.5 h-4 w-4 shrink-0', s.tone === 'warn' ? 'text-clay' : 'text-brand-500')}
                />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        )
      ) : null}

      {s.note && (
        <p className="flex items-start gap-2 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">
          <Icon name="ShieldCheck" className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
          {s.note}
        </p>
      )}
    </div>
  );
}

/** Build accordion items from the backend detail sections (in authored order). */
export function sectionsToAccordionItems(sections: DetailSection[]): AccordionItem[] {
  return (sections ?? [])
    .filter((s) => s && s.title)
    .map((s, i) => {
      const title = s.title;
      const openable = /dosage|dose|composition|formula|specification/i.test(title);
      return {
        id: s.id || `sec-${i}`,
        title,
        icon: iconFor(title, s.kind),
        defaultOpen: s.defaultOpen ?? (i === 0 || openable),
        content: renderSection(s),
      };
    });
}
